<template>
  <div class="container">
    <header>
      <div class="title-banner">
        <h1 class="gb-page-title">Browse Assessments</h1>
        <p v-if="assessmentWizardEnabled" class="title-subheading">Discover targeted baseline and progress monitoring assessments from thousands of resources in our collection.</p>
        <p v-else class="title-subheading">Search through our entire collection of baseline and progress monitoring assessments.</p>
      </div>
      <div v-if="isFromWizard" class="return-button-container">
        <button class="return-button" @click="returnToWizard">
          <i class="material-icons-round arrow-left">arrow_back</i>
          Return to Wizard
        </button>
      </div>
    </header>
    <main>
      <ais-instant-search
        v-if="searchClient"
        :key='searchClientRefreshedAt'
        :search-client="searchClient"
        :index-name="algoliaIndex"
        :middlewares="middlewares"
        :on-state-change="updateState"
        :routing="routing"
      >
        <ais-configure
          v-bind="limitOffsetProps()"
          :optional-words.camel="optionalQueryWords"
          :analytics="true"
          :click-analytics.camel="true"
          :filters="characteristicFilters"
        />
        <div class="row">
          <div class="col-md-3">
            <BrowseAssessmentsFilters
              :algolia-index="algoliaIndex"
              :show-grades="!hideGrades"
              :loading="loading"
              :search-client="searchClient"
              :on-updated-query="onUpdatedQuery"
              :selected-subject="selectedSubject"
              :keyword-query="keywordQuery"
              :category-options="categoryOptions"
              :default-subjects="allSubjects"
              :search-box-ref-name="searchBoxRefName"
              :selected-language.sync="selectedLanguage"
            />
          </div>
          <div class="col-md-9">
            <ais-current-refinements
              :transform-items="transformRefinementLabels"
              :excluded-attributes="[]">
              <template v-slot:item="{ item, refine, createURL }">
                <span class="ais-CurrentRefinements-label" v-if="item.label != 'Categories'">{{ item.label }}: </span>
                <span
                  v-for="refinement in item.refinements"
                  :key="[ refinement.attribute, refinement.type, refinement.value ].join(':')"
                  class="ais-CurrentRefinements-category"
                >
                  {{ refinement.label }}
                  <a
                    :href="createURL(refinement)"
                    @click.prevent="onClearFilterEvent(refinement, refine)"
                    :aria-label="refinement.label"
                  >
                    <i class="fa fa-close" />
                  </a>
                </span>
              </template>
            </ais-current-refinements>
            <ais-stats v-if="showHits">
              <template v-slot="{nbHits}">
                <small class="text-muted text-small">{{ prepareResultCountText(nbHits) }}</small>
              </template>
            </ais-stats>
            <BrowseAssessmentsResults
              v-if="hasAgenticResults"
              ref="browseAssessmentsResults"
              :items="agenticResults"
              :send-event="()=>{}"
              :selected-subject="selectedSubject"
              :category-options="categoryOptions"
              :show-category-headers="showCategoryHeaders"
            />
            <ais-state-results v-else>
              <template v-slot="{ state: { }, results: { hits } }">
                <div v-if="!showHits && hits.length">
                  <ul class="list-unstyled">
                    <li class="empty-state-tips">Select a subject from the dropdown</li>
                    <li class="empty-state-tips">Filter by grade levels</li>
                    <li class="empty-state-tips">Choose a category for the subject selected above</li>
                    <li class="empty-state-tips">Search using keywords</li>
                    <li class="empty-state-tips">Filter by assessment type</li>
                    <li class="empty-state-tips">Filter by language</li>
                  </ul>
                </div>
                <div v-if="!hits.length">
                  <p class="no-results">Sorry, we currently don't have any assessments matching your query; try broadening your search by de-selecting some criteria above.</p>
                </div>
              </template>
            </ais-state-results>
            <div class="ais-Hits" v-if="loading && !showHits"><i class="fa fa-spin fa-circle-o-notch" /> Loading...</div>
            <ais-hits v-else-if="!loading && showHits && !hasAgenticResults">
              <template v-slot="{ items, sendEvent }">
                <BrowseAssessmentsResults
                  ref="browseAssessmentsResults"
                  :items="items.slice(0, maxPremiumResults)"
                  :send-event="sendEvent"
                  :selected-subject="selectedSubject"
                  :category-options="categoryOptions"
                  :show-category-headers="showCategoryHeaders"
                />
              </template>
            </ais-hits>
          </div>
        </div>
      </ais-instant-search>
    </main>
    <SubmitFeedback
      type="Browse Assessments"/>
  </div>
</template>

<script>
import algoliasearch from 'algoliasearch/lite';
import 'instantsearch.css/themes/satellite-min.css';
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';
import { AisStats, AisRefinementList, AisPanel, AisHits, AisCurrentRefinements, AisConfigure, AisStateResults, AisSortBy } from 'vue-instantsearch';
import SubmitFeedback from '@/components/SubmitFeedback';
import AccessPointLabel from '@/components/goals/AccessPointLabel';
import {
  createInsightsMiddleware
} from 'instantsearch.js/es/middlewares';
import aa from 'search-insights';
import { mapGetters, mapActions } from 'vuex';
import BrowseAssessmentsFilters from '@/components/search/assessments/BrowseAssessmentsFilters';
import BrowseAssessmentsResults from '@/components/search/assessments/BrowseAssessmentsResults';
import { debouncer } from '@/util/debouncer'
import { intForGrade } from '@/util/gradesort'
import { isCoreSubject } from '@/util/core_subject'
import eventHub from '@/util/event_hub'
import { CreateAlgoliaKeyProviderMixin } from '@/mixins/AlgoliaKeyProvider.js'
import { BetaFeatureMixin } from '@/mixins/BetaFeatureMixin'
import { noSelMixin } from '@/contentprofile/no_sel_mixins'


const appID = process.env.VUE_APP_ALGOLIA_APP_ID;
const algoliaIndexBase = process.env.VUE_APP_ALGOLIA_ALL_ASSESSMENTS_INDEX;
const algoliaIndexSortKeyReplica = `${algoliaIndexBase}_sort_key_asc`
const numResultsForNonPremiumUsers = 2;

export default {
  name: 'BrowseAssessments',
  components: {
      AccessPointLabel,
      AisCurrentRefinements,
      AisConfigure,
      AisHits,
      AisPanel,
      AisRefinementList,
      AisStats,
      AisStateResults,
      SubmitFeedback,
      AisSortBy,
      BrowseAssessmentsFilters,
      BrowseAssessmentsResults,
  },
  mixins: [
    CreateAlgoliaKeyProviderMixin('browse_assessments', [algoliaIndexBase, algoliaIndexSortKeyReplica]),
    noSelMixin
  ],
  methods: {
    ...mapActions('agenticSearch', ['clearResults']),
    returnToWizard() {
      this.clearResults()
      this.$router.push('/wizard/assessments')
    },
    clearAgenticResults() {
      this.clearResults()
    },
    onClearFilterEvent(refinement, refine) {
      refine(refinement);
      let eventAttributes = {}
      if (refinement.attribute === 'grades') {
        eventAttributes['browse_assessments_removed_grade'] = refinement.label
        this.$sendEvent('Removed Browse Assessments Grade', eventAttributes)

      } else if (refinement.attribute === 'domains') {
        eventAttributes['browse_assessments_removed_category'] = refinement.label
        this.$sendEvent('Removed Browse Assessments Category', eventAttributes)

      } else if (refinement.attribute === 'subject') {
        eventAttributes['browse_assessments_removed_subject'] = refinement.label
        this.$sendEvent('Removed Browse Assessments Subject', eventAttributes)

      } else if (refinement.attribute === 'type') {
        eventAttributes['browse_assessments_removed_type'] = refinement.label
        this.$sendEvent('Removed Browse Assessments Type', eventAttributes)

      } else if (refinement.attribute === 'language') {
        eventAttributes['browse_assessments_removed_language'] = refinement.label
        this.$sendEvent('Removed Browse Assessments Language', eventAttributes)

      } else if (refinement.attribute === 'query') {
        eventAttributes['browse_assessments_removed_keyword'] = refinement.label.slice(0, 120) // limit length due to size of db field
        this.$sendEvent('Removed Browse Assessments Keyword', eventAttributes)
        eventHub.$emit('clear-keyword', this.searchBoxRefName)
      }
    },
    async getAndStoreCategoryOptions(category) {
      let subjectSkills = await getSkillBySubject(category, '', '')
      if (!this.categoryOptions) {
        this.categoryOptions = {}
      }
      this.categoryOptions[subjectSkills['subject']] = subjectSkills['skills'].sort((a, b) => parseInt(a.skill_order) - parseInt(b.skill_order))
      return this.categoryOptions[subjectSkills['subject']]
    },
    transformRefinementLabels(items) {
      const orderedFacets = ['subject', 'domains', 'behavior_category', 'grades', 'type', 'language', 'query']
      let labelMap = {
        domains: 'Categories',
        query: 'Keywords',
        behavior_category: 'Categories',
        type: 'Assessment',
        language: 'Language'
      }
      return items.map((item) => {
        if (item.label === 'grades') {
          item.refinements = item.refinements.sort((a, b) => intForGrade(a.value) - intForGrade(b.value))
        }
        if (item.attribute === 'language') {
          item.refinements = item.refinements.filter(r => r.value.toLowerCase() !== 'english')
        }
        return {
          ...item,
          label: labelMap[item.label] ? labelMap[item.label] : item.label,
        }
      }).filter(item => item.refinements.length > 0).sort(function(a, b) {
        return orderedFacets.indexOf(a.attribute) - orderedFacets.indexOf(b.attribute);
      });
    },
    onUpdatedQuery(newQuery) {
      if (newQuery) {
        this.$sendEvent('Searched Browse Assessments Keyword', {browse_assessments_searched_keyword: newQuery.slice(0, 120)}) //limit length due to max size of field in db
      } else {
        this.$sendEvent('Cleared Browse Assessments Keyword')
      }
      debouncer(() => {
        this.optionalQueryWords = newQuery
      }, 500)
    },
    clearCategoriesAndGrades(uiState, clearAllGrades) {
      delete uiState[this.algoliaIndex].refinementList?.grades
      delete uiState[this.algoliaIndex].menu?.domains
      delete uiState[this.algoliaIndex].menu?.behavior_category
      return uiState
    },
    updateState({ uiState, setUiState }) {
      this.clearAgenticResults()
      // uiState is an object documented here https://www.algolia.com/doc/api-reference/widgets/ui-state/vue/
      this.$refs.browseAssessmentsResults?.sendReturnedResultsAnalyticsEvent()
      const newSubject = uiState[this.algoliaIndex].menu?.subject
      const subjectChanged = newSubject !== this.selectedSubject
      if (subjectChanged) {
        const clearGrades = !newSubject
        const currentLanguage = uiState[this.algoliaIndex].menu?.language
        this.clearCategoriesAndGrades(uiState, clearGrades)
        this.selectedSubject = newSubject
        // Set language to English when subject is removed or when a new subject is selected with no language
        if (!newSubject || (newSubject && !currentLanguage)) {
          if (!uiState[this.algoliaIndex].menu) {
            uiState[this.algoliaIndex].menu = {}
          }
          uiState[this.algoliaIndex].menu.language = 'English'
        }
      }
      this.selectedType = uiState[this.algoliaIndex].menu?.type
      this.selectedLanguage = uiState[this.algoliaIndex].menu?.language || 'English'
      this.keywordQuery = uiState[this.algoliaIndex].query
      uiState[this.algoliaIndex].sortBy = this.keywordQuery ? algoliaIndexBase : algoliaIndexSortKeyReplica

      // Ensure language is always set (in case it is deselected)
      if (!uiState[this.algoliaIndex].menu?.language) {
        if (!uiState[this.algoliaIndex].menu) {
          uiState[this.algoliaIndex].menu = {}
        }
        uiState[this.algoliaIndex].menu.language = this.selectedLanguage || 'English'
      }

      // Clear filters for non-premium users
      if (!this.isPremiumUser) {
        delete uiState[this.algoliaIndex].query
        delete uiState[this.algoliaIndex].menu?.domains
        delete uiState[this.algoliaIndex].menu?.behavior_category
        delete uiState[this.algoliaIndex].refinementList?.grades
      }

      setUiState(uiState)
    },
    limitOffsetProps() {
      let propsToPass = {}
      if (!this.isPremiumUser) {
        propsToPass.length = numResultsForNonPremiumUsers
        propsToPass.offset = '0'
      }
      return propsToPass
    },
    prepareResultCountText(nbHits) {
      if (this.isPremiumUser && nbHits < this.maxPremiumResults) {
        return `Found ${nbHits} results`
      } else if (this.isPremiumUser) {
        return `Found more than ${this.maxPremiumResults} results`
      } else {
        let howManyMoreHits = nbHits - numResultsForNonPremiumUsers
        if (howManyMoreHits > 1) {
          return numResultsForNonPremiumUsers + ' results. (' + howManyMoreHits + ' more with premium)'
        } else {
          return nbHits + ' results';
        }
      }
    },
    setSearchClient(apiKey) {
      const insightsMiddleware = createInsightsMiddleware({
        insightsClient: aa,
        insightsInitParams: {
          appId: appID,
          apiKey: apiKey,
          useCookie: true // needed so we can generate a userToken for algolia insights events when user is not logged in
        }
      })
      this.middlewares = [insightsMiddleware]
      this.searchClient = algoliasearch(appID, apiKey)
      this.searchClientRefreshedAt = Date.now()
    },
  },
  computed: {
    ...mapGetters({
      userId: 'user/userId',
      contentProfileSubjects: 'contentProfile/publishedSubjects',
      selVisibility: 'visibility/selVisibility',
      isPremiumUser: 'user/isPremium',
      agenticResults: 'agenticSearch/assessmentResults',
      hasAgenticResults: 'agenticSearch/hasAssessmentResults',
      agenticLoading: 'agenticSearch/loading',
      agenticError: 'agenticSearch/error',
      assessmentWizardEnabled: 'visibility/assessmentWizardVisibility',
    }),
    characteristicFilters() {
      // Only exclude BLV and DHH characteristics when NOT doing a keyword search
      // Keyword searches should show all results including BLV/DHH resources
      if (this.keywordQuery) {
        return ''
      }
      const CHARACTERISTIC_ID_BLV = 'recjUHa6goKzM1jnr'
      const CHARACTERISTIC_ID_DHH = 'recw811h4t0eAEah9'
      return `NOT characteristics:${CHARACTERISTIC_ID_BLV} AND NOT characteristics:${CHARACTERISTIC_ID_DHH}`
    },
    showHits() {
      return this.selectedSubject || this.keywordQuery
    },
    isFromWizard() {
      return this.$route.query.ref === 'wizard'
    },
    isSubjectSelected() { return !!this.selectedSubject },
    isBehaviorSelected() { return this.selectedSubject === 'Behavior'},
    allSubjects() { return this.assessmentSubjectOptionsForProfile },
    userToken() { return this.userId || ''},
    routing() {
      const index = this.algoliaIndex
      return {
        router: historyRouter(),
        stateMapping: {
          stateToRoute(uiState) {
            return {
              query: uiState[index].query,
              subject: uiState[index].menu?.subject,
              category: uiState[index].menu?.domains || uiState[index].menu?.behavior_category,
              grades:
                uiState[index].refinementList &&
                uiState[index].refinementList?.grades &&
                uiState[index].refinementList?.grades.join('~'),
              type: uiState[index].menu?.type,
              language: uiState[index].menu?.language || 'English'
            };
          },
          routeToState(routeState) {
            let newRouteState = {
              [index]: {
                query: routeState.query,
                menu: {
                  subject: routeState.subject,
                  domains: routeState.category,
                  type: routeState.type,
                  language: routeState.language || 'English'
                },
                refinementList: {
                  grades: routeState.grades && routeState.grades?.split('~'),
                },
              },
            };
            if (routeState.subject === 'Behavior') {
              newRouteState[index].menu.behavior_category = routeState.category
            }
            if (!routeState.query) {
              newRouteState[index].sortBy = algoliaIndexSortKeyReplica
            }
            return newRouteState;
          },
        }
      }
    },
    hideGrades() {
      return !this.selectedSubject || (this.$route.query?.type === 'Baseline' && !isCoreSubject(this.selectedSubject))
    }
  },
  data() {
    return {
      searchClient: null,
      algoliaIndex: algoliaIndexBase,
      searchClientRefreshedAt: Date.now(),
      middlewares: [],
      optionalQueryWords: '',
      showCategoryHeaders: true,
      categoryOptions: null,
      selectedSubject: this.$route.query?.subject,
      selectedType: this.$route.query?.type,
      selectedLanguage: this.$route.query?.language || 'English',
      keywordQuery: '',
      loading: false,
      searchBoxRefName: 'assessmentsKeywordInput',
      maxPremiumResults: 300
    };
  },
  mounted() {
    this.$sendEvent('Viewed Browse Assessments Page', {
      ref: this.$route.query.ref
    })
  },
  async created() {
    aa('setUserToken', this.userToken);
    this.$on('refreshedAlgoliaKey', apiKey => {
      this.setSearchClient(apiKey)
    })
  },
  watch: {
    userId() {
      aa('setUserToken', this.userToken);
    }
  },
  eventContext() {
    return {
      browse_assessments_selected_type: this.selectedType
    }
  }
};
</script>

<style lang="less" scoped>
@import '~@/variables.less';

.return-button-container {
  display: flex;
  justify-content: center;
  margin: 24px 0 12px 0;
}

.return-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 24px;
  border: 1px solid #007dba;
  border-radius: 4px;
  background: transparent;
  color: #007dba;
  font-weight: 400;
  font-size: 16px;
  line-height: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: rgba(0, 125, 186, 0.05);
  }

  &:focus {
    outline: 2px solid #007dba;
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #007dba;
    outline-offset: 2px;
  }

  .arrow-left {
    font-size: 24px;
    line-height: 1;
  }
}

.gb-page-title {
  clear: both;
}
.ais-InstantSearch::v-deep {
  padding-top: 1.5rem;
  .ais-Panel {
    margin-bottom: 1rem;
  }
  .filter-header {
    font-size: 1rem;
    font-weight: 700;
    line-height: 20px;
    color: @gray;
    margin: 0 0 5px 0;
    padding: 5px 0;
    text-transform: uppercase;
  }
  .empty-state-tips {
    font-weight: 300;
    margin: 25px 0 55px 0;
    font-size: 1.6rem;
    line-height: 40px;
  }
  .no-results {
    background-color: @blueLight;
    color: @blue;
    padding: 10px 35px 10px 15px;
    border: 1px solid @tealLight;
  }
  .fa-close {
    color: @blue;
    cursor: pointer;
    margin-left: 7px;
  }

  .ais-Stats {
    padding-bottom: 15px;
  }
  .ais-CurrentRefinements {
    border-bottom: 2px solid @gray-lighter;
  }

  .ais-CurrentRefinements-list {
    padding-bottom: 10px;
    grid-auto-flow: unset;
    gap: unset;
    display: unset;
  }
  .ais-CurrentRefinements-item {
    border: none;
    background: none;
    display: inline-flex;
    padding-right: 0;
  }
  .ais-CurrentRefinements-item + .ais-CurrentRefinements-item:has(.ais-CurrentRefinements-label) {
    display: flex;
    padding-top: 5px;
  }
  .ais-CurrentRefinements-label {
    text-transform: capitalize;
  }
  .ais-CurrentRefinements-category {
    background-color: @blueLight;
    padding: 5px 10px 5px 10px;
    border-radius: 5px;
    border: solid 1px @blue;
  }
  .ais-CurrentRefinements-delete {
    color: @blue;
    cursor: pointer;
    margin-left: 7px;
    height: unset;
  }
}
</style>
